import { useEffect, useRef, useState } from "react";

import { captureEvent } from "components/posthog/customEventCapture";
import { Icon } from "components/icon/Icon";

import "./ContextualMenu.scss";

type MenuItem = {
  label: string;
  onClick: () => void;
  persistOnClick?: boolean;
  iconName?: string;
  disabled?: boolean;
};

type ContextualMenuProps = {
  menuItems: MenuItem[];
  buttonText?: string;
  buttonIconName?: string;
  className?: string;
  position?: "left" | "right" | "center";
};

function ContextualMenu({
  menuItems,
  className = "",
  buttonText = "",
  buttonIconName = "",
  position = "left"
}: ContextualMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  const toggleMenu = () => setIsOpen(!isOpen);
  const closeMenu = () => setIsOpen(false);

  const handleOnClick = (item: MenuItem) => {
    if (!item.disabled) {
      item.onClick();
      if (!item.persistOnClick) {
        closeMenu();
      }
    }
  };

  const renderMenuItemContent = (item: MenuItem) =>
    item.iconName ? (
      <div className="d-flex align-items-center">
        <Icon name={item.iconName} className="mr-3 text-link" />
        <span>{item.label}</span>
      </div>
    ) : (
      <span>{item.label}</span>
    );

  const renderButtonContent = () => (
    <div className={`${className} ${buttonIconName ? "d-flex" : ""}`}>
      {buttonIconName && <Icon name={buttonIconName} className="menu-icon" />}
      {buttonText && <span>{buttonText}</span>}
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        closeMenu();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  return (
    <div className="contextualMenu" ref={menuRef}>
      <button
        className={`${className} contextualMenu-button`}
        onClick={() => {
          toggleMenu();
          captureEvent(buttonText);
        }}
      >
        {renderButtonContent()}
      </button>
      {isOpen && (
        <div className={`contextualMenu-container ${position}`}>
          {menuItems.map((item) => (
            <div
              key={item.label}
              className={`menu-item ${item.disabled ? "disabled" : ""}`}
              onClick={() => handleOnClick(item)}
            >
              {renderMenuItemContent(item)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default ContextualMenu;
